import * as React from 'react';
import { Checkbox, Form, Heading, Input } from '@built/design-system';
import { Link } from 'react-router-dom';
import { Password } from './PasswordBase';
import ActionButton from '../containers/ActionButton';
import Notification from '../containers/Notification';
import EmailField from '../containers/EmailField';
import {
  AuthData,
  AuthError,
  BrandingData,
  AuthErrorType,
  ValidationErrorType
} from '../types';
import * as styles from './Auth.module.scss';
import builtLogo from '../images/img-built-logo.svg';
import * as ErrorNotifications from './ErrorNotifications';
import { getConfig, getValidationError } from '../utils';
import l10n from '@built/i18n';

/**
 * Based on the current auth state, renders one of:
 *  - Logout button
 *  - Password update field
 *  - MFA code entry field
 *  - Email and Password fields for logging in
 */
export default function Auth(props: AuthProperties) {
  const { authError, className } = props;
  const authData = props.authData || {};
  const classes = [styles.verticalSpacing];
  if (className) {
    classes.push(className);
  }

  return (
    <div className={classes.join(' ')}>
      {renderHeading(props)}
      {renderLogoutStatus(props)}

      {authError && !authData.needsMfa && renderError(props)}

      {renderView(props)}
    </div>
  );
}

function getRememberHint(props: AuthProperties): string {
  return l10n.t(
    'Auth:Mfa.rememberMeMessage',
    `Remembering this device will let you skip multi-factor
      authentication the next time you login with this device.`
  );
}

function renderMfaVerificationError(
  authData: AuthData | undefined,
  authError: AuthError
) {
  return (
    <Notification
      type="warning"
      title={l10n.t(
        'Auth:Mfa.errors.verificationErrorTitle',
        'Verification error'
      )}
    >
      <p>
        {l10n.t(
          'Auth:Mfa.errors.verificationErrorMessage',
          `Your verification data didn't match up with what we expected. Check
            your answers and try again.`
        )}
      </p>
    </Notification>
  );
}

function renderMfaResendSmsError(
  authData: AuthData | undefined,
  authError: AuthError
) {
  return (
    <Notification
      type="warning"
      title={l10n.t('Auth:Mfa.errors.resendSmsErrorTitle', 'Resend error')}
    >
      <p>
        {l10n.t(
          'Auth:Mfa.errors.resendSmsErrorMessage',
          'We were unable to resend your code via SMS. Please try again.'
        )}
      </p>
    </Notification>
  );
}

/**
 * Render any errors that may have occurred during a previous
 * login attempt
 */
function renderError(props: AuthProperties) {
  const { authData, authError: _authError } = props;
  const authError = _authError!;
  const errorType = props.authError!.type;

  switch (errorType) {
    case AuthErrorType.REGISTRATION_ERROR:
    case AuthErrorType.EMAIL_VERIFICATION_ERROR:
    case AuthErrorType.MOCK_COGNITO_ERROR:
    case AuthErrorType.PASSWORD_RESET_REQUIRED_ERROR:
    case AuthErrorType.ATTEMPTS_EXCEEDED_ERROR:
    case AuthErrorType.SAML_RESPONSE_ERROR:
    case AuthErrorType.UNVERIFIED_USER_ERROR:
      return ErrorNotifications.genericError(authError, 'danger', false);
    case AuthErrorType.ACCOUNT_DISABLED:
      return ErrorNotifications.accountDisabledError(authError);
    case AuthErrorType.ACCOUNT_EXPIRED:
      return ErrorNotifications.accountExpiredError(authError);
    case AuthErrorType.INVALID_CREDENTIALS_ERROR:
      return ErrorNotifications.invalidCredentialsError(authError);
    case AuthErrorType.MFA_VERIFICATION_ERROR:
      return renderMfaVerificationError(authData, authError);
    case AuthErrorType.MFA_RESEND_SMS_ERROR:
      return renderMfaResendSmsError(authData, authError);
    case AuthErrorType.UNKNOWN_ERROR:
    default:
      return ErrorNotifications.unknownError();
  }
}

function getMfaErrorMessage(props: AuthProperties) {
  const authError = props.authError || { type: '', message: '' };
  const errorType = authError!.type;
  const errorMsg = authError.message || null;
  switch (errorType) {
    case AuthErrorType.MFA_VERIFICATION_ERROR:
      return errorMsg
        ? errorMsg
        : l10n.t(
            'Auth:Mfa.errors.invalidCodeError',
            'Please check the code you entered and try again.'
          );
    case AuthErrorType.MFA_RESEND_SMS_ERROR:
      return l10n.t(
        'Auth:Mfa.errors.resendSmsUnknownError',
        'We encountered an error while trying to resend your code. Please try again.'
      );
    default:
      return l10n.t(
        'Auth:Mfa.errors.unknownError',
        'We encountered an unexpected error while trying to process your request. Please login in again.'
      );
  }
}

function renderHeading(props: AuthProperties) {
  const useMockCognito = getConfig('REACT_APP_USE_MOCK_COGNITO') === 'true';
  const { authData } = props;

  let heading: string;
  if (authData && authData.welcomeBack) {
    heading = l10n.t('Auth:Heading.welcomeBack', 'Welcome back.');
  } else if (authData && authData.emailVerification) {
    heading = l10n.t('Auth:Heading.verifyEmail', 'Verify new email address');
  } else if (authData && authData.isImaUser) {
    heading = l10n.t(
      'Auth:Heading.securityUpgrade',
      'Account security upgrade required'
    );
  } else {
    heading = l10n.t('Auth:Heading.logIn', 'Log In');
  }

  return (
    <React.Fragment>
      <Heading
        size="xxl"
        className={[styles.loginHeader, 'margin-b-md'].join(' ')}
      >
        {heading}
      </Heading>

      {useMockCognito && (
        // @i18n-ignore
        <Notification type="warning" title="Mock Cognito Enabled">
          <p>
            Your environment is configured to use mock cognito mode which will
            allow any email/password combination and generate fake JWT tokens.
            For this to work, you will also need to enable mock cognito mode in
            your built-dashboards.
          </p>
        </Notification>
      )}
    </React.Fragment>
  );
}

function renderLogoutStatus(props: AuthProperties) {
  if (!props.authData || !props.authData.logoutStatus) {
    return;
  }

  return (
    <Notification
      type="danger"
      title={l10n.t('Auth:Notification.logout', 'You have been logged out')}
    >
      <p>{props.authData.logoutStatus}</p>
    </Notification>
  );
}

function renderInitialPasswordForm(props: AuthProperties) {
  return (
    <form className={styles.form} onClick={props.setInitialPassword}>
      <Password
        autoFocus={true}
        autoComplete="off"
        onChange={props.onInputChange}
        disabled={props.busy}
        name="password"
        label={l10n.t('Auth:password', 'Password')}
        size="lg"
      />
      <ActionButton
        loading={props.busy}
        label={l10n.t('Auth:setPassword', 'Set password')}
        type="submit"
      />
    </form>
  );
}

function renderMfaCodeForm(props: AuthProperties) {
  const rememberHint = getRememberHint(props);
  const authData = props.authData || {};
  const mfaParams = authData.mfaParams || {};
  const deliveryMethod = mfaParams.CODE_DELIVERY_DELIVERY_MEDIUM || 'TOTP';
  const enableRememberMe =
    getConfig('REACT_APP_MFA_ENABLE_REMEMBER_ME', 'false') === 'true';

  let error = props.authError ? getMfaErrorMessage(props) : null;
  let subtext;

  switch (deliveryMethod) {
    case 'SMS':
      const len = authData.mfaParams.CODE_DELIVERY_DESTINATION.length;
      const last4 = authData.mfaParams.CODE_DELIVERY_DESTINATION.substring(
        len - 4
      );

      subtext = (
        <p className={styles.resendWrapper}>
          {l10n.t(
            'Auth:Mfa.enterSmsVerificationCodeMessage1',
            'Enter the code we just sent to the number ending in'
          )}{' '}
          <span className={styles.numberText}>{last4}</span>{' '}
          {l10n.t(
            'Auth:Mfa.enterSmsVerificationCodeMessage2',
            "via SMS. Didn't receive a code?"
          )}{' '}
          <ActionButton
            buttonType="link"
            label={l10n.t(
              'Auth:Mfa.requestNewVerificationCodeButtonLabel',
              'Request a new code'
            )}
            className={styles.resendButton}
            onClick={props.onResendSmsCode}
          />
        </p>
      );
      break;

    default:
      // totp
      subtext = (
        <p className={styles.resendWrapper}>
          {l10n.t(
            'Auth:Mfa.enterTotpVerificationCodeMessage',
            'Enter the code displayed in your authenticator app.'
          )}
        </p>
      );
      break;
  }

  return (
    <form className={styles.form} onSubmit={props.sendMfaCode}>
      <Input
        autoFocus={true}
        onChange={props.onInputChange}
        disabled={props.busy}
        name="code"
        label={l10n.t('Auth:Mfa.verificationCodeLabel', 'Verification code')}
        value={props.code}
        size="lg"
        subtext={error}
        className={error ? styles.error : undefined}
      />
      {subtext}

      <ActionButton
        loading={props.busy}
        label={l10n.t(
          'Auth:Mfa.confirmVerificationCodeButtonLabel',
          'Confirm Code'
        )}
        type="submit"
        className="margin-b-lg"
      />

      {enableRememberMe && (
        <Form.Group subtext={rememberHint}>
          <Checkbox
            name="remember"
            checked={props.remember}
            label={l10n.t(
              'Auth:Mfa.rememberMeCheckboxLabel',
              'Remember this device?'
            )}
            onChange={props.onInputChange}
            className={styles.rememberCheckbox}
          />
        </Form.Group>
      )}
    </form>
  );
}

function renderLoginForm(props: AuthProperties) {
  const { branding } = props;
  const passwordError = props.showPasswordError
    ? getValidationError(ValidationErrorType.VALIDATION_PASSWORD_ERROR)
    : '';
  const passwordClassName = passwordError ? styles.passwordError : '';

  // We need to show the SSO login link if 1) we don't have branding data (which
  // very likely means we're using the generic login UX) or 2) the branding data
  // includes the link to redirec to (requires appropriate branding and bank
  // settings to be configured)
  const showSsoLink =
    branding === null || !!(branding && branding.externalLoginUrl);

  return (
    <form className={styles.form} onSubmit={props.login}>
      <EmailField
        autoFocus={true}
        disabled={props.busy}
        onChange={props.onEmailAddressChange}
        name="email"
        showError={props.showEmailError}
        showSsoLink={showSsoLink}
        ssoUrl={props.externalLoginUrl}
        value={props.email}
        trackLoginSSO={props.trackLoginSSO}
      />

      <Password
        autoComplete="off"
        className={passwordClassName}
        disabled={props.busy}
        label={l10n.t('Auth:Label.Password', 'Password')}
        name="password"
        onChange={props.onInputChange}
        size="lg"
        subtext={passwordError}
        value={props.password}
      />

      <p className={styles.forgotPassword}>
        <Link
          className={styles.linkNaked}
          to="/reset"
          onClick={props.trackForgotPassword}
        >
          {l10n.t('Auth:Message.forgotPassword', 'Forgot password?')}
        </Link>
      </p>

      <div className={styles.loginButtonPoweredBy}>
        <ActionButton
          label={l10n.t('Auth:Label.logIn', 'Log In')}
          loading={props.busy}
          type="submit"
        />
        {renderPoweredBy(props)}
      </div>
    </form>
  );
}

function renderImaUserLoginForm(props: AuthProperties) {
  const passwordError = props.showPasswordError
    ? getValidationError(ValidationErrorType.VALIDATION_PASSWORD_ERROR)
    : '';
  const passwordClassName = passwordError ? styles.passwordError : '';
  return (
    <form className={styles.form} onSubmit={props.login}>
      <p className={styles.lead}>
        {l10n.t(
          'Auth:Message.toProtect',
          'To better protect your account and secure your information,' +
            ` we're requiring all users to reset their password using our new security standards.` +
            ` To continue, please enter your `
        )}
        <strong>
          {l10n.t('Auth:Message.currentPassword', 'current password')}
        </strong>
        .
      </p>

      <Input
        label={l10n.t('Auth:Label.email', 'Email Address')}
        disabled={true}
        name="email"
        size="lg"
        value={props.email}
      />

      <Password
        autoComplete="off"
        className={passwordClassName}
        disabled={props.busy}
        label={l10n.t('Auth:Label.currentPasswordMayus', 'Current Password')}
        name="password"
        onChange={props.onInputChange}
        size="lg"
        subtext={passwordError}
        value={props.password}
      />

      <div className={styles.loginButtonPoweredBy}>
        <ActionButton
          label={l10n.t('Auth:Label.continue', 'Continue')}
          loading={props.busy}
          type="submit"
        />
        {renderPoweredBy(props)}
      </div>
    </form>
  );
}

function renderEmailVerification(props: AuthProperties) {
  return (
    <React.Fragment>
      <Notification
        type="success"
        title={l10n.t(
          'Auth:Notification.title.emailVerified',
          'Your email address has been verified'
        )}
      >
        {l10n.t('Auth:Notification.nowUseEmail', 'You will now use the email')}{' '}
        <span className="emailAddress">{props.authData!.email}</span>
        {l10n.t('Auth:Notification.eachtime', 'each time you log into Built.')}
      </Notification>
      <ActionButton
        href="/"
        label={l10n.t('Auth:Label.continueBuilt', 'Continue to Built')}
        buttonType="primary"
      />
    </React.Fragment>
  );
}

function renderPoweredBy(props: AuthProperties) {
  return (
    <React.Fragment>
      {props.branding && (
        <div className={styles.loginLogo}>
          {l10n.t('Auth:Message.poweredBy', 'Powered by')}
          <img src={builtLogo} alt="Built Logo" />
        </div>
      )}
    </React.Fragment>
  );
}

/**
 * Render the appropriate form based on the AuthData state.
 */
// TODO (abe) this needs to be simplified
// tslint:disable-next-line:cyclomatic-complexity
function renderView(props: AuthProperties) {
  const { authData } = props;

  if (!authData) {
    return renderLoginForm(props);
  }

  if (authData.newPasswordRequired) {
    return renderInitialPasswordForm(props);
  } else if (authData.needsMfa) {
    return renderMfaCodeForm(props);
  } else if (authData.emailVerification) {
    return renderEmailVerification(props);
  } else if (authData.isImaUser) {
    return renderImaUserLoginForm(props);
  } else {
    return renderLoginForm(props);
  }
}

export interface AuthProperties {
  /**
   * The authorization/authentication data for the currently logged in user
   */
  authData?: AuthData;

  /** An error that happened during the last authentication attempt */
  authError?: AuthError;

  /** If true, a login is in-progress */
  busy?: boolean;

  /** An optional additional class name for the authorization component */
  className?: string;

  /** The user's email address */
  email: string;

  /** Is the email value a valid email address? */
  emailValid: boolean;

  /** The user's password */
  password: string;

  /** The current value of a code input field */
  code?: string;

  /** If true, remeber the current device */
  remember?: boolean;

  branding?: BrandingData;

  /** Where to send the user for SSO */
  externalLoginUrl?: string;

  /** */
  showEmailError?: boolean;
  showPasswordError?: boolean;

  /** Event handler to log a user out */
  logout(): void;

  /** Event handler to log a user in */
  login(event: React.SyntheticEvent<HTMLElement>): void;

  /** Callback to send a user's MFA code */
  sendMfaCode(event: React.SyntheticEvent<HTMLElement>): void;

  /** Callback to resend a user's MFA code */
  onResendSmsCode(event: React.SyntheticEvent<HTMLElement>): void;

  /** Callback to set the user's new password */
  setInitialPassword(event: React.SyntheticEvent<HTMLElement>): void;

  /** Event handler to handle changes in input elements */
  onInputChange(event: React.SyntheticEvent<HTMLInputElement>): void;

  /** EmailField change handler */
  onEmailAddressChange(email: string, valid: boolean): void;

  /** Prop to call when a user submits the welcome back form */
  onWelcomeBackSubmit(event: React.SyntheticEvent<HTMLElement>): void;

  /** Track snowplow event when a user click on "Forgot Password" */
  trackForgotPassword(): void;

  /** Track snowplow event when a user clicks "Login with SSO" */
  trackLoginSSO(): void;
}
